import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useHover, useLayer } from "react-laag";
import styled, { css } from "styled-components";
import { useFocusRing } from "@react-aria/focus";
import { LightTheme, DarkTheme } from "./themeContainers.js";
import { textStyles } from "./textStyles.js";
import { token } from "./token.js";
import { Flex } from "./Flex.js";
import { useZIndex } from "./useZIndex.js";
import { Image } from "./Image.js";
import { Text } from "./Text4.js";
import { ListItem } from "./ListItem4.js";
import { fixLayerProps } from "./fixLayerProps.js";
function Tooltip({
  position = "top-center",
  timing = "instant",
  size = "small",
  isLightTheme = false,
  className,
  icon,
  secondaryText,
  badge,
  content,
  ...props
}) {
  const [isHoveredOver, hoverProps] = useHover({
    delayEnter: timing === "delay" ? 800 : 0
  });
  const {
    isFocusVisible,
    focusProps
  } = useFocusRing();
  const zIndex = useZIndex("tooltip");
  const isOpen = props.isOpen === void 0 ? isHoveredOver || isFocusVisible : props.isOpen;
  const {
    triggerProps,
    layerProps,
    renderLayer
  } = useLayer({
    isOpen,
    auto: true,
    snap: true,
    placement: getPlacement(position),
    possiblePlacements: getPossiblePlacements(position),
    triggerOffset: 8,
    containerOffset: 8
  });
  fixLayerProps(layerProps);
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [typeof props.children === "function" ? props.children({
      ...triggerProps,
      ...hoverProps,
      ...focusProps
    }) : /* @__PURE__ */ jsx("div", {
      ...triggerProps,
      ...hoverProps,
      ...focusProps,
      children: props.children
    }), isOpen && renderLayer(/* @__PURE__ */ jsx(ThemeWrapper, {
      isLightTheme,
      children: /* @__PURE__ */ jsx(TooltipContainer, {
        ...layerProps,
        $zIndex: zIndex,
        className,
        $size: size,
        children: /* @__PURE__ */ jsxs(Flex, {
          alignItems: "center",
          gap: "3",
          children: [icon && /* @__PURE__ */ jsx(IconContainer, {
            $size: size,
            children: icon
          }), /* @__PURE__ */ jsx("div", {
            children: content
          }), secondaryText && /* @__PURE__ */ jsx(SecondaryTextContainer, {
            children: secondaryText
          }), badge]
        })
      })
    }))]
  });
}
function getPlacement(position) {
  const positionToPlacementMap = {
    "top-left": "top-start",
    "top-center": "top-center",
    "top-right": "top-end",
    left: "left-center",
    right: "right-center",
    "bottom-left": "bottom-start",
    "bottom-center": "bottom-center",
    "bottom-right": "bottom-end"
  };
  return positionToPlacementMap[position];
}
function getPossiblePlacements(position) {
  const topBottomPlacements = ["top-center", "bottom-center"];
  const leftRightPlacements = ["left-center", "right-center"];
  const cornerPlacements = ["top-end", "top-start", "bottom-end", "bottom-start"];
  const positionToPossiblePlacementsMap = {
    "top-left": cornerPlacements,
    "top-center": topBottomPlacements,
    "top-right": cornerPlacements,
    left: leftRightPlacements,
    right: leftRightPlacements,
    "bottom-left": cornerPlacements,
    "bottom-center": topBottomPlacements,
    "bottom-right": cornerPlacements
  };
  return positionToPossiblePlacementsMap[position];
}
function ThemeWrapper({
  isLightTheme,
  children
}) {
  return isLightTheme ? /* @__PURE__ */ jsx(LightTheme, {
    children
  }) : /* @__PURE__ */ jsx(DarkTheme, {
    children
  });
}
Tooltip.Image = Image;
Tooltip.Text = Text;
Tooltip.ListItem = ListItem;
const TooltipContainer = styled.div.withConfig({
  displayName: "TooltipContainer",
  componentId: "sc-xel4md-0"
})(["--ds-tooltip-border-radius:", ";z-index:", ";display:flex;box-sizing:border-box;border-radius:var(--ds-tooltip-border-radius);background:", ";color:", ";box-shadow:", ";word-break:break-word;", ";"], token.radiusBase, (props) => props.$zIndex, token.componentTooltipBackground, token.componentTooltipTitleColor, token.componentTooltipShadow, (props) => {
  if (props.$size === "small") {
    return css(["", " max-width:240px;padding:", " ", ";"], textStyles.uiTextS, token.spacing3, token.spacing4);
  } else if (props.$size === "medium") {
    return css(["", " max-width:256px;padding:", " ", ";"], textStyles.uiTextM, token.spacing3, token.spacing5);
  }
});
const IconContainer = styled.span.withConfig({
  displayName: "IconContainer",
  componentId: "sc-xel4md-1"
})(["display:flex;align-items:center;width:24px;height:24px;margin-block:calc(", " * -1);margin-left:", ";"], token.spacing1, (props) => props.$size === "medium" ? `calc(${token.spacing3} * -1)` : `calc(${token.spacing2} * -1)`);
const SecondaryTextContainer = styled.span.withConfig({
  displayName: "SecondaryTextContainer",
  componentId: "sc-xel4md-2"
})(["", " color:", ";"], textStyles.uiTextS, token.componentTooltipSecondaryTextColor);
export {
  Tooltip
};
//# sourceMappingURL=Tooltip.js.map
